/**
 * @fileoverview Écran de confirmation avant redirection guichet.
 *
 * Le panier est reconstruit depuis l'URL (`?liq=LIQ-1,LIQ-2`) transmise par
 * la page de recherche — règle du guide : la survie d'état passe par l'URL,
 * jamais par le storage navigateur. Sans sélection valide, l'écran renvoie
 * vers la recherche plutôt que d'afficher un panier fantôme.
 *
 * Au branchement API, le bouton « Continuer » déclenchera `POST /payments`
 * avec une Idempotency-Key UUIDv7 générée AU CLIC, puis une redirection
 * `window.location.assign(payment_url)` vers le guichet Paymetrust. En
 * maquette, il navigue vers l'écran de retour avec un identifiant fixe.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, PaymentSummary, PortalState } from '@gnpc/ui';
import { ExternalLink } from 'lucide-react';
import { MOCK_CONTRAVENTIONS } from '../data/mock.ts';

export function PaymentPage(): JSX.Element {
  const [params] = useSearchParams();

  // Reconstruction du panier depuis `?liq=` — seuls les numéros connus du
  // référentiel sont retenus (un LIQ falsifié dans l'URL est ignoré).
  const selectedItems = useMemo(() => {
    const requested = (params.get('liq') ?? '')
      .split(',')
      .map((value) => decodeURIComponent(value.trim()))
      .filter(Boolean);
    if (requested.length === 0) return [];
    const requestedSet = new Set(requested);
    return MOCK_CONTRAVENTIONS.filter((item) => requestedSet.has(item.liquidationNumber));
  }, [params]);

  const totalAmount = selectedItems.reduce((sum, item) => sum + item.amount, 0);

  if (selectedItems.length === 0) {
    return (
      <div className="mx-auto grid max-w-xl gap-5">
        <PortalState
          status="empty"
          // En accès direct sans sélection, cet état EST le contenu
          // principal de la page : son titre porte le `<h1>` (axe
          // `page-has-heading-one`).
          titleAs="h1"
          title="Aucune contravention sélectionnée"
          action={
            <Button asChild variant="outline">
              <Link to="/recherche">Retour à la recherche</Link>
            </Button>
          }
        >
          Sélectionnez au moins une contravention depuis la recherche pour préparer un
          paiement.
        </PortalState>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-xl gap-5">
      <header className="space-y-2">
        <p className="text-quipux-orange-600 text-sm font-medium">Paiement</p>
        <h1 className="text-quipux-navy-950 text-3xl font-semibold tracking-tight">
          Confirmer avant de payer
        </h1>
        <p className="text-muted-foreground text-sm leading-6">
          Le choix de l'opérateur mobile money se fait sur une page de paiement sécurisée.
        </p>
      </header>

      <PaymentSummary selectedCount={selectedItems.length} totalAmount={totalAmount} />

      <ul className="border-border grid gap-2 rounded-lg border bg-white p-4 text-sm">
        {selectedItems.map((item) => (
          <li key={item.liquidationNumber} className="flex items-baseline justify-between gap-3">
            <span className="font-mono text-xs text-quipux-navy-900">
              {item.liquidationNumber}
            </span>
            <span className="tabular-nums text-quipux-navy-950">
              {item.amount.toLocaleString('fr-FR')} F CFA
            </span>
          </li>
        ))}
      </ul>

      <div className="grid gap-3">
        <Button asChild size="lg">
          <Link to="/paiement/retour?txn=019f620c-8151-7722-a0d4-bfb775612106">
            <ExternalLink />
            Continuer vers le paiement
          </Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/recherche">Retour à la sélection</Link>
        </Button>
      </div>
    </div>
  );
}
